// -----------------------------------------------------------------------------
// AuthPages.tsx — v0.8.0 sign-in helper screens (round 28 boards A0.1, A0.2), shown by App.tsx before the sign-in gate.
//   ForgotPasswordPage (A0.1)  email → "Send reset link" → "Check your email" (never says whether the email has a
//                              login, so nobody can use it to find out who works here) · Send again · Back to sign in
//   SetPasswordPage (A0.2)     the invite / reset email's link: opens the link (signs in), then New + Confirm password
//                              → "Set password and sign in". A used or >24 h link shows "This link has expired" with
//                              "Email me a new link" (a reset email; an invite that expired needs Resend invite).
// Backend calls: core/api/backend.ts `auth` (Supabase Auth live; the dev server's outbox locally).
// Same card as A0 Sign in (LoginPage.tsx); only the two (i) on A0.2 (round 23: A0 itself has none).
// -----------------------------------------------------------------------------
import { useEffect, useState } from "react";
import { auth, ExpiredLinkError, type EmailLinkType } from "../api/backend";
import { firstName, passwordProblems } from "../authLinks";
import { EMAIL_KEY } from "../rememberMe";
import { ErrorBox, InfoIcon, Loading } from "../ui/kit";
import { VersionLine } from "../layout/Shell";
import { Icon } from "../ui/icons";
import { BrandMark, BrandWordmark } from "../ui/brand";

const TIP_PASSWORD = ["At least 8 characters, with letters and numbers.", "The two boxes must match.", "After saving you are signed in and land on Projects."];
const TIP_EXPIRED = ["Links work for 24 hours and only once.", "Reset link: ask for a new one here.", "Invite link: ask your administrator or project head to Resend invite."];
const toSignIn = () => { window.location.hash = "#/"; };

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div className="login">
      <div className="login-card auth-card">
        <div className="brand big"><BrandMark size={40} /><BrandWordmark /></div>
        {children}
      </div>
      <VersionLine />
    </div>
  );
}
const Back = () => <button type="button" className="btn link accent auth-back" onClick={toSignIn}><Icon name="arrow_back" /> Back to sign in</button>;

/** The email box + "Send reset link" (A0.1), also the expired-link card's "Email me a new link" (A0.2). */
function useResetSender() {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const send = async (email: string) => {
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) { setErr("Enter your work email"); return false; }
    setBusy(true); setErr(null);
    try { await auth.sendPasswordReset(email.trim()); return true; }
    catch (e) { setErr((e as Error).message); return false; }
    finally { setBusy(false); }
  };
  return { busy, err, send };
}

function SentNote({ email }: { email: string }) {
  return <>
    <h2 className="login-title">Check your email</h2>
    <p className="note-ok" data-testid="reset-sent">If <b>{email.trim()}</b> has a BuildNexx login, a reset link is on its way. It works for 24 hours.</p>
    <p className="muted auth-text">Nothing after a few minutes? Check the spam folder, or ask your administrator.</p>
  </>;
}

export function ForgotPasswordPage() {
  const [email, setEmail] = useState(() => { try { return localStorage.getItem(EMAIL_KEY) ?? ""; } catch { return ""; } });
  const [sent, setSent] = useState(false);
  const { busy, err, send } = useResetSender();
  const go = async () => { if (await send(email)) setSent(true); };
  return (
    <Card>
      {!sent ? (
        <form className="auth-form" noValidate onSubmit={(e) => { e.preventDefault(); go(); }} data-testid="forgot-form">
          <h2 className="login-title">Reset your password</h2>
          <p className="muted auth-text">Enter your work email. We will email you a link to set a new password.</p>
          <label className="field"><span>Email</span><input type="email" autoFocus autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} /></label>
          {err && <ErrorBox message={err} />}
          <button className="btn primary wide" type="submit" disabled={busy}>{busy ? "Sending…" : "Send reset link"}</button>
          <Back />
        </form>
      ) : (
        <div className="auth-form">
          <SentNote email={email} />
          {err && <ErrorBox message={err} />}
          <button className="btn wide" disabled={busy} onClick={() => send(email)}>Send again</button>
          <Back />
        </div>
      )}
    </Card>
  );
}

function PasswordBox({ label, value, onChange, tip, autoFocus }: { label: string; value: string; onChange: (v: string) => void; tip?: string[]; autoFocus?: boolean }) {
  const [show, setShow] = useState(false);
  return (
    <label className="field"><span className="field-label">{label}{tip && <InfoIcon tip={tip} />}</span>
      <span className="pw-wrap"><input type={show ? "text" : "password"} autoComplete="new-password" autoFocus={autoFocus} value={value} onChange={(e) => onChange(e.target.value)} aria-label={label} />
        <button type="button" className="pw-eye" aria-label={show ? "Hide password" : "Show password"} onClick={() => setShow((v) => !v)}><Icon name={show ? "visibility_off" : "visibility"} /></button></span>
    </label>
  );
}

export function SetPasswordPage({ tokenHash, type, onDone }: { tokenHash: string | null; type: EmailLinkType | null; onDone: () => void }) {
  const [who, setWho] = useState<{ email: string; name: string } | null>(null);
  const [expired, setExpired] = useState(!tokenHash);
  const [pw, setPw] = useState(""); const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const reset = useResetSender();
  useEffect(() => {
    if (!tokenHash || !type) return;
    let gone = false;
    auth.openEmailLink(tokenHash, type)
      .then((w) => { if (!gone) { setWho(w); setEmail(w.email); } })
      .catch((e) => { if (gone) return; if (e instanceof ExpiredLinkError) setExpired(true); else setErr((e as Error).message); });
    return () => { gone = true; };
  }, [tokenHash, type]);
  const save = async () => {
    const problems = passwordProblems(pw, pw2);
    if (problems.length) return setErr(problems.join(" · "));
    setBusy(true); setErr(null);
    try { await auth.setNewPassword(pw); window.location.hash = "#/"; onDone(); }
    catch (e) { if (e instanceof ExpiredLinkError) setExpired(true); else setErr((e as Error).message); setBusy(false); }
  };
  if (expired) return (
    <Card>
      {sent ? <div className="auth-form"><SentNote email={email} /><Back /></div> : (
        <form className="auth-form" noValidate data-testid="link-expired" onSubmit={async (e) => { e.preventDefault(); if (await reset.send(email)) setSent(true); }}>
          <h2 className="login-title row gap6">This link has expired <InfoIcon tip={TIP_EXPIRED} /></h2>
          <p className="note-warn">For your security, each link works once, for 24 hours.</p>
          <label className="field"><span>Email</span><input type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} /></label>
          {reset.err && <ErrorBox message={reset.err} />}
          <button className="btn primary wide" type="submit" disabled={reset.busy}>Email me a new link</button>
          <Back />
        </form>)}
    </Card>
  );
  if (!who) return <Card>{err ? <><ErrorBox message={err} /><Back /></> : <Loading />}</Card>;
  const invite = type === "invite";
  return (
    <Card>
      <form className="auth-form" noValidate data-testid="set-password-form" onSubmit={(e) => { e.preventDefault(); save(); }}>
        <h2 className="login-title">{invite ? `Welcome to BuildNexx${who.name ? ", " + firstName(who.name) : ""}` : "Set a new password"}</h2>
        <p className="muted auth-text">{invite ? "Set a password for " : "New password for "}<b className="ink">{who.email}</b>.</p>
        <PasswordBox label="New password" value={pw} onChange={(v) => { setPw(v); setErr(null); }} tip={TIP_PASSWORD} autoFocus />
        <PasswordBox label="Confirm password" value={pw2} onChange={(v) => { setPw2(v); setErr(null); }} />
        {err && <ErrorBox message={err} />}
        <button className="btn primary wide" type="submit" disabled={busy}>{busy ? "Saving…" : "Set password and sign in"}</button>
      </form>
    </Card>
  );
}
